/** Byte helpers shared across the crypto package. Pure, environment-independent. */
export function concat(...parts) {
    let len = 0;
    for (const p of parts)
        len += p.length;
    const out = new Uint8Array(len);
    let off = 0;
    for (const p of parts) {
        out.set(p, off);
        off += p.length;
    }
    return out;
}
/** Constant-time equality for equal-length inputs; false for differing lengths. */
export function equal(a, b) {
    if (a.length !== b.length)
        return false;
    let diff = 0;
    for (let i = 0; i < a.length; i++)
        diff |= a[i] ^ b[i];
    return diff === 0;
}
const encoder = new TextEncoder();
const decoder = new TextDecoder();
export function utf8(s) {
    return encoder.encode(s);
}
export function fromUtf8(b) {
    return decoder.decode(b);
}
/** Big-endian unsigned 32-bit integer. */
export function u32be(n) {
    if (!Number.isInteger(n) || n < 0 || n > 0xffffffff)
        throw new RangeError("u32 out of range");
    const b = new Uint8Array(4);
    new DataView(b.buffer).setUint32(0, n);
    return b;
}
/** Big-endian unsigned 64-bit integer from a safe JS integer. */
export function u64be(n) {
    if (!Number.isSafeInteger(n) || n < 0)
        throw new RangeError("u64 out of range");
    const b = new Uint8Array(8);
    new DataView(b.buffer).setBigUint64(0, BigInt(n));
    return b;
}
/** Length-prefixed framing so that concatenated fields cannot be ambiguously re-split. */
export function frame(...fields) {
    const parts = [];
    for (const f of fields)
        parts.push(u32be(f.length), f);
    return concat(...parts);
}
const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
const B64_LOOKUP = {};
for (let i = 0; i < B64.length; i++)
    B64_LOOKUP[B64.charAt(i)] = i;
export function toBase64Url(bytes) {
    let out = "";
    let i = 0;
    for (; i + 2 < bytes.length; i += 3) {
        const n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
        out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + B64[(n >> 6) & 63] + B64[n & 63];
    }
    if (i < bytes.length) {
        const rem = bytes.length - i;
        const n = (bytes[i] << 16) | (rem === 2 ? bytes[i + 1] << 8 : 0);
        out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63];
        if (rem === 2)
            out += B64[(n >> 6) & 63];
    }
    return out;
}
export function fromBase64Url(s) {
    if (!/^[A-Za-z0-9_-]*$/.test(s))
        throw new Error("invalid base64url");
    const rem = s.length % 4;
    if (rem === 1)
        throw new Error("invalid base64url length");
    const outLen = Math.floor((s.length * 3) / 4);
    const out = new Uint8Array(outLen);
    let o = 0;
    let i = 0;
    for (; i + 3 < s.length; i += 4) {
        const n = (B64_LOOKUP[s.charAt(i)] << 18) |
            (B64_LOOKUP[s.charAt(i + 1)] << 12) |
            (B64_LOOKUP[s.charAt(i + 2)] << 6) |
            B64_LOOKUP[s.charAt(i + 3)];
        out[o++] = (n >> 16) & 255;
        out[o++] = (n >> 8) & 255;
        out[o++] = n & 255;
    }
    if (rem >= 2) {
        const n = (B64_LOOKUP[s.charAt(i)] << 18) |
            (B64_LOOKUP[s.charAt(i + 1)] << 12) |
            (rem === 3 ? B64_LOOKUP[s.charAt(i + 2)] << 6 : 0);
        out[o++] = (n >> 16) & 255;
        if (rem === 3)
            out[o++] = (n >> 8) & 255;
    }
    return out;
}
export function toHex(bytes) {
    let s = "";
    for (const b of bytes)
        s += b.toString(16).padStart(2, "0");
    return s;
}
/** Overwrite a secret buffer. Best effort; JS engines may keep copies. */
export function wipe(...buffers) {
    for (const b of buffers)
        b.fill(0);
}
//# sourceMappingURL=bytes.js.map